import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, CheckboxGroup, Loader, Modal } from 'rsuite';
import { ButtonPrimary } from '../../../../../../components/ButtonPrimary';
import { SearchInput } from '../../../../../../components/SearchInput';
import { useEngineUpdateCandidateUsersQuery } from '../hooks/useEngineUpdateCandidateUsersQuery';
import { EngineUpdateCandidateUser } from '../types';
import styles from '../SetupIpaEngineUpdateNotifications.module.scss';

type UserSelectionModalProps = {
  show: boolean;
  selectedUserIds: number[];
  onCancel: () => void;
  onConfirm: (users: EngineUpdateCandidateUser[]) => void;
};

const countLabel = (count: number) => {
  if (count === 0) return 'Nenhum usuário selecionado';
  if (count === 1) return '1 usuário selecionado';
  return `${count} usuários selecionados`;
};

export const UserSelectionModal = ({ show, selectedUserIds, onCancel, onConfirm }: UserSelectionModalProps) => {
  const { data, isLoading, isError } = useEngineUpdateCandidateUsersQuery();
  const [search, setSearch] = useState('');
  const [checkedIds, setCheckedIds] = useState<number[]>(selectedUserIds);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (show && !wasOpenRef.current) {
      setCheckedIds(selectedUserIds);
      setSearch('');
    }
    wasOpenRef.current = show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const candidates = useMemo(() => [...(data ?? [])].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })), [data]);

  const filteredCandidates = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pt-BR');
    if (!normalizedSearch) return candidates;

    return candidates.filter(
      candidate =>
        candidate.name.toLocaleLowerCase('pt-BR').includes(normalizedSearch) || candidate.email.toLocaleLowerCase('pt-BR').includes(normalizedSearch),
    );
  }, [candidates, search]);

  // AC-9: sem candidatos carregados não há o que confirmar; salvar aqui zeraria a lista de destinatários.
  const isCandidatesUnavailable = isLoading || isError;

  const handleConfirm = () => {
    if (isCandidatesUnavailable) return;
    onConfirm(candidates.filter(candidate => checkedIds.includes(candidate.cdUsuario)));
  };

  const renderList = () => {
    if (isError) return <div className={styles['modal-feedback']}>Não foi possível carregar os usuários.</div>;
    if (isLoading) return <Loader center content="Carregando usuários..." />;
    if (filteredCandidates.length === 0) {
      const emptyMessage = search.trim() ? `Nenhum usuário encontrado para "${search}".` : 'Nenhum usuário encontrado.';
      return <p className={styles['modal-feedback']}>{emptyMessage}</p>;
    }

    return (
      <CheckboxGroup value={checkedIds} onChange={value => setCheckedIds(value as number[])}>
        {filteredCandidates.map(candidate => (
          <Checkbox key={candidate.cdUsuario} className={styles['candidate-option']} value={candidate.cdUsuario}>
            <span className={styles['candidate-identity']}>
              <strong>{candidate.name}</strong>
              <small>{candidate.email}</small>
            </span>
          </Checkbox>
        ))}
      </CheckboxGroup>
    );
  };

  return (
    <Modal className={styles['user-selection-modal']} show={show} onHide={onCancel} size="sm" backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>Selecionar usuários</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className={styles['modal-support']}>
          Escolha os usuários que receberão o aviso de atualização do motor por e-mail. Você pode selecionar mais de um.
        </p>

        <SearchInput
          aria-label="Buscar usuário por nome ou e-mail"
          className={styles['modal-search']}
          placeholder="Buscar por nome ou e-mail"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />

        <div className={styles.listbox}>{renderList()}</div>

        <p className={styles['selection-count']}>{countLabel(checkedIds.length)}</p>
      </Modal.Body>

      <Modal.Footer>
        <ButtonPrimary size="medium" theme="ghost" type="button" onClick={onCancel}>
          cancelar
        </ButtonPrimary>
        <ButtonPrimary size="medium" type="button" disabled={isCandidatesUnavailable} onClick={handleConfirm}>
          salvar
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
};
